import React, { useState, useEffect } from 'react';
import { LuGlobe, LuTarget, LuArrowRight, LuCalendar, LuBookOpen, LuUsers, LuHeart, LuSparkles, LuSun } from 'react-icons/lu';
import { GiChurch, GiHolySymbol } from 'react-icons/gi';
import hnhHero from '../../assets/hnh.png';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';
import { extractString } from '../../shared/fieldUtils';

export default function History({ activeSection, onSelectSection, isEmbedded = false }) {
  const [bishopData, setBishopData] = useState({
    name: '',
    img_url: ''
  });

  useEffect(() => {
    let isMounted = true;
    async function fetchBishop() {
      try {
        const snap = await getDocs(collection(db, 'bishop')).catch(() => null);
        if (snap && !snap.empty) {
          const doc = snap.docs[0].data();
          if (isMounted) {
            setBishopData({
              name: extractString(doc.name || doc.bishop_name, ''),
              img_url: extractString(doc.img_url || doc.image, '')
            });
          }
        }
      } catch (err) {
        console.error("Error fetching bishop for history:", err);
      }
    }
    fetchBishop();
    return () => { isMounted = false; };
  }, []);

  const visionPillars = [
    { title: "Renewed Churches", desc: "Churches renewed through prayer, worship, unity and faithful service.", icon: GiChurch },
    { title: "Strengthened Families", desc: "Families strengthened through Christian love, fellowship and spiritual formation.", icon: LuHeart },
    { title: "Empowered Youth", desc: "Youth empowered to become disciples, leaders and witnesses of Christ.", icon: LuUsers },
    { title: "Transformed Communities", desc: "Communities transformed through compassion, justice, peace and hope.", icon: LuSun },
    { title: "Gospel Proclaimed", desc: "The Gospel proclaimed through word and action across all walks of life.", icon: LuSparkles },
    { title: "Creation Cared For", desc: "Creation cared for through responsible ecological stewardship.", icon: LuGlobe },
  ];

  return (
    <div className={`bg-[#FAF7F2] text-[#5F5A58] font-sans selection:bg-[#7B1E3A]/20 selection:text-[#7B1E3A] flex flex-col pb-16 ${!isEmbedded ? 'min-h-screen' : 'pt-8'}`}>
      
      {/* ── 1. Hero Header Banner ── */}
      {!isEmbedded && (
      <section className="relative w-full overflow-hidden min-h-[380px] sm:min-h-[420px] lg:min-h-[440px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={hnhHero}
            alt="CSI Diocese History & Heritage"
            className="w-full h-full object-cover object-center filter brightness-[0.75] contrast-[1.08]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#140e1b]/90 via-[#140e1b]/40 to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 sm:pt-32 pb-16 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
          <div className="max-w-2xl text-left">
            <h1 className="font-serif font-bold text-white text-[38px] sm:text-[48px] lg:text-[56px] tracking-[0.04em] uppercase leading-[1.05] drop-shadow-md">
              HISTORY &amp; HERITAGE
            </h1>
            <p className="text-[#E8B86D] text-[15px] sm:text-[17px] font-medium tracking-wide mt-2.5 drop-shadow-sm">
              175+ years of Protestant heritage, theological foundation, and visionary Christian mission in Northern Malabar.
            </p>
            <div className="flex items-center gap-2 text-xs text-white/80 font-medium mt-4">
              <span onClick={() => onSelectSection('home')} className="hover:text-white cursor-pointer transition-colors">Home</span>
              <span className="text-[#E8B86D] font-bold">›</span>
              <span onClick={() => onSelectSection('about')} className="hover:text-white cursor-pointer transition-colors">About Diocese</span>
              <span className="text-[#E8B86D] font-bold">›</span>
              <span className="text-white">History &amp; Heritage</span>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-10 sm:h-12 text-[#FAF7F2] fill-current">
            <path d="M0,0 C150,90 350,-40 500,60 C650,160 900,10 1200,40 L1200,120 L0,120 Z"></path>
          </svg>
        </div>
      </section>
      )}

      <div className={`max-w-6xl mx-auto px-6 sm:px-8 space-y-16 ${!isEmbedded ? 'mt-16' : ''}`}>
        
        {/* Intro */}
        <div>
          <h2 className="font-serif font-bold text-[#7B1E3A] text-3xl sm:text-4xl lg:text-[42px] leading-tight mb-8">
            History of the Church of South India
          </h2>
          <h3 className="font-serif font-bold text-[#333A46] text-xl sm:text-2xl mb-4">A Historic Journey of Unity</h3>
          <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans mb-5">
            The Church of South India (CSI) is one of the most remarkable expressions of Christian unity in the modern world. Formed through prayer, dialogue, and a shared commitment to the Gospel, CSI brought together churches from different traditions into one united Church in Christ.
          </p>
          <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans mb-5">
            The movement toward union began in 1919, when Indian Christian leaders met at Tranquebar with a vision to unite the divided Protestant churches in South India into one indigenous Church. Their dream was to witness to Christ through unity, worship, and service in a newly emerging India.
          </p>
          <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans">
            After nearly thirty years of theological discussions, prayer, and consultation, this vision was fulfilled on 27 September 1947, when the Church of South India was inaugurated.
          </p>
        </div>

        {/* About Us Traditions */}
        <div className="bg-white p-8 sm:p-10 rounded-[24px] border border-[#E8DDD7] shadow-sm">
          <h3 className="font-serif font-bold text-[#7B1E3A] text-xl sm:text-2xl mb-4">About Us</h3>
          <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans mb-6">
            CSI united churches from four major traditions:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {['Anglican', 'Methodist', 'Presbyterian', 'Congregational'].map(trad => (
              <div key={trad} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4A64A]"></span>
                <span className="text-[#333A46] font-medium font-sans">{trad}</span>
              </div>
            ))}
          </div>
          <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans">
            Churches shaped by the Basel Mission tradition later became part of this wider fellowship, enriching the Church’s heritage in South India.
          </p>
        </div>

        {/* The First Union */}
        <div>
          <h3 className="font-serif font-bold text-[#333A46] text-xl sm:text-2xl mb-4">The First Union of Its Kind</h3>
          <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans mb-6">
            The Church of South India holds a unique place in global Christian history as the first union of episcopal and non-episcopal churches after the Reformation. It became a model of ecumenical unity for churches around the world.
          </p>
          <h4 className="font-sans font-bold text-[#7B1E3A] text-[15px] uppercase tracking-wider mb-4">CSI was formed with a clear calling:</h4>
          <ul className="space-y-3 text-[#5F5A58] text-[16px] sm:text-[17px] font-sans mb-6">
            {[
              'To proclaim the Gospel of Jesus Christ',
              'To strengthen Christian witness in India',
              'To serve society through compassion and justice',
              'To embody unity amid diversity'
            ].map(calling => (
              <li key={calling} className="flex items-start gap-3">
                <LuTarget className="w-5 h-5 text-[#D4A64A] shrink-0 mt-1" />
                <span>{calling}</span>
              </li>
            ))}
          </ul>
          <div className="bg-[#FAF6F2] p-6 rounded-2xl border-l-4 border-[#7B1E3A]">
            <p className="text-[#243B5C] font-serif italic text-lg mb-1">
              This historic union continues to stand as a witness to Christ’s prayer:
            </p>
            <p className="text-[#7B1E3A] font-serif font-bold text-xl">“That they all may be one.” (John 17:21)</p>
          </div>
        </div>

        {/* CSI Today */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <h3 className="font-serif font-bold text-[#333A46] text-xl sm:text-2xl mb-4">The Church of South India Today</h3>
            <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans mb-6">
              Today, the Church of South India remains one of the largest Protestant churches in Asia. CSI serves across:
            </p>
            <div className="flex flex-wrap gap-2 mb-6">
              {['Andhra Pradesh', 'Telangana', 'Karnataka', 'Kerala', 'Tamil Nadu', 'Puducherry', 'Jaffna (Sri Lanka)', 'Global Diaspora'].map(region => (
                <span key={region} className="px-3 py-1 bg-[#FAF6F2] border border-[#E8DDD7] rounded-full text-sm font-medium text-[#5F5A58]">
                  {region}
                </span>
              ))}
            </div>
            <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans">
              The Church also has worshipping communities and congregations across North America, Europe, Australia, the Gulf region, Singapore, and Malaysia.
            </p>
          </div>
          <div className="bg-[#7B1E3A] p-8 rounded-2xl text-white shadow-lg flex flex-col justify-between">
            <div>
              <h4 className="font-serif font-bold text-xl mb-6 text-white/90">As of 2026, the Church includes:</h4>
              <ul className="space-y-4 font-sans font-medium">
                <li className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-white/70">Dioceses</span>
                  <span className="text-white font-bold text-xl">26</span>
                </li>
                <li className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-white/70">Congregations</span>
                  <span className="text-white font-bold text-xl">~15,000</span>
                </li>
                <li className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-white/70">Members</span>
                  <span className="text-white font-bold text-xl">4.5M+</span>
                </li>
                <li className="flex justify-between pb-2">
                  <span className="text-white/70">Clergy & Presbyters</span>
                  <span className="text-white font-bold text-xl">~3,500</span>
                </li>
              </ul>
            </div>
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-sm text-white/80 leading-relaxed">
                The CSI is currently led by <strong className="text-white">The Most Rev. Dr. K. Reuben Mark</strong>, Moderator of the Church of South India.
              </p>
            </div>
          </div>
        </div>

        {/* ── Section 1: The Diocesan Bishop ── */}
        <div className="bg-white p-8 sm:p-10 rounded-[32px] border border-[#E8DDD7] shadow-[0_10px_35px_rgba(123,30,58,0.05)]">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <span className="text-[#D4A64A] font-sans font-bold text-xs uppercase tracking-[0.2em] mb-2 block">
              EPISCOPAL LEADERSHIP
            </span>
            <h3 className="font-serif font-bold text-[#7B1E3A] text-2xl sm:text-3xl">
              1. Diocesan Bishop
            </h3>
          </div>

          <div className="flex flex-col md:flex-row gap-8 items-center md:items-start bg-[#FAF7F2] p-6 sm:p-8 rounded-[24px] border border-[#E8DDD7]">
            <div className="w-44 h-56 sm:w-52 sm:h-64 rounded-2xl overflow-hidden shadow-md border-2 border-[#E8DDD7] shrink-0 bg-white">
              <img 
                src={bishopData.img_url} 
                alt={bishopData.name} 
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="flex-1 space-y-4 text-left">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#7B1E3A]/10 text-[#7B1E3A] rounded-full text-xs font-bold uppercase tracking-wider mb-2">
                  <GiHolySymbol className="w-3.5 h-3.5" />
                  First Bishop of Malabar
                </span>
                <h4 className="font-serif font-bold text-[#7B1E3A] text-2xl sm:text-3xl">
                  {bishopData.name}
                </h4>
                <p className="text-[#D4A64A] font-bold text-sm uppercase tracking-wider flex items-center gap-2 mt-1">
                  <LuCalendar className="w-4 h-4" />
                  Consecrated: 17 May 2016
                </p>
              </div>

              <p className="text-[#5F5A58] text-[15px] sm:text-[16px] leading-[1.85]">
                <strong>{bishopData.name}</strong> serves as the Bishop of the Church of South India Diocese of Malabar and was consecrated as the first Bishop of the Diocese on 17 May 2016. Under his episcopal leadership, the Diocese seeks to grow as a vibrant community of worship, witness and service.
              </p>

              <p className="text-[#5F5A58] text-[15px] sm:text-[16px] leading-[1.85]">
                His ministry is characterised by a commitment to spiritual renewal, pastoral care, Christian formation, mission and compassionate engagement with society. He encourages the Church to remain firmly rooted in the Gospel while responding faithfully to the changing realities and needs of the world. His episcopal vision seeks to strengthen congregations, families and young people and to encourage the Church to be a visible witness to Christ through service, compassion, justice and hope.
              </p>

              <div className="pt-2">
                <button
                  onClick={() => onSelectSection('bishop')}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#7B1E3A] hover:bg-[#5E132A] text-white text-xs font-bold uppercase tracking-wider rounded-full transition-all duration-300 shadow-sm cursor-pointer"
                >
                  <span>Visit Bishop's Office</span>
                  <LuArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 2: Theology: Rooted in Christ, Scripture and Mission ── */}
        <div className="bg-gradient-to-br from-[#FAF5EF] via-[#F8F0E7] to-[#F1E5D8] rounded-[32px] p-6 sm:p-10 lg:p-12 border border-[#E8DDD7] shadow-[0_10px_35px_rgba(0,0,0,0.03)] text-left">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/80 text-[#7B1E3A] px-3.5 py-1.5 rounded-full text-xs font-bold border border-[#E8DDD7] mb-3">
              <LuBookOpen className="w-4 h-4 text-[#D4A64A]" />
              <span>Our Theological Foundation</span>
            </div>

            <h2 className="font-serif font-bold text-[#7B1E3A] text-2xl sm:text-3xl lg:text-[34px] leading-tight mb-2">
              2. Theology
            </h2>
            <h3 className="font-serif font-bold text-[#2D2420] text-lg sm:text-xl mb-6">
              Rooted in Christ, Scripture and Mission
            </h3>

            <div className="space-y-5 text-[#4A3F3B] text-[15px] sm:text-[16px] leading-[1.85]">
              <p>
                The theological foundation of the Diocese of Malabar is centred on Jesus Christ, the Holy Scriptures and the mission of God. The Bible is regarded as the foundation of the life of the Diocese, calling every believer to read, study and meditate upon God's Word.
              </p>

              <p>
                We believe that Christian faith is not confined to worship within the Church but is expressed through a life of discipleship, love, compassion, justice and service. The Church is called to proclaim the Gospel, nurture people in faith, care for the vulnerable, promote peace and reconciliation, and participate in God's transforming work in the world.
              </p>

              <p>
                Our theology therefore brings together worship and witness, faith and action, spiritual formation and social responsibility. As a united Church within the Church of South India, we also value Christian unity and ecumenical fellowship as an essential expression of our witness to Christ.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 pt-6 border-t border-[#E8DDD7]">
              <div className="bg-white/80 p-4 rounded-2xl border border-white/60">
                <span className="text-xs font-bold uppercase tracking-wider text-[#7B1E3A] block mb-1">Christ-Centred</span>
                <p className="text-xs text-[#5F5A58]">Rooted in Jesus Christ and guided by the Holy Scriptures.</p>
              </div>
              <div className="bg-white/80 p-4 rounded-2xl border border-white/60">
                <span className="text-xs font-bold uppercase tracking-wider text-[#7B1E3A] block mb-1">Faith in Action</span>
                <p className="text-xs text-[#5F5A58]">Discipleship expressed through compassion, justice, and service.</p>
              </div>
              <div className="bg-white/80 p-4 rounded-2xl border border-white/60">
                <span className="text-xs font-bold uppercase tracking-wider text-[#7B1E3A] block mb-1">Christian Unity</span>
                <p className="text-xs text-[#5F5A58]">Committed to ecumenical fellowship and united witness in CSI.</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 3: Vision: Walking in the Gospel: A Journey of Renewal ── */}
        <div className="bg-gradient-to-br from-[#842343] via-[#7B1E3A] to-[#5A1428] rounded-[32px] p-6 sm:p-10 lg:p-12 text-white shadow-xl relative overflow-hidden text-left">
          <div className="relative z-10 max-w-5xl">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E8B86D] block mb-2">
              DIOCESAN VISION
            </span>

            <h2 className="font-serif font-bold text-3xl sm:text-4xl lg:text-[40px] leading-tight mb-2">
              3. Vision
            </h2>
            <h3 className="font-serif font-bold text-2xl sm:text-3xl text-[#E8B86D] mb-4">
              Walking in the Gospel: A Journey of Renewal
            </h3>

            <p className="text-white/90 text-[15px] sm:text-[16px] leading-[1.8] mt-4 mb-4">
              The vision of the Diocese of Malabar is to be a Christ-centred, spiritually renewed and mission-oriented Church, proclaiming the Gospel and serving God and society with faithfulness and compassion.
            </p>

            <div className="bg-white/10 backdrop-blur-md p-5 rounded-2xl border border-white/15 my-6">
              <p className="text-[#E8B86D] font-serif font-bold text-lg mb-2">
                2026 Year of Renewal Theme: “Walking in the Gospel: A Journey of Renewal”
              </p>
              <p className="text-white/85 text-[14px] leading-relaxed">
                In 2026, the Diocese observes the Year of Renewal under the theme <em>“Walking in the Gospel: A Journey of Renewal.”</em> This vision calls the Church to awaken its faith, renew its commitment and rediscover its mission. Renewal begins with the transformation of the heart and mind through God's Word and Spirit and extends into the life of the Church and society.
              </p>
            </div>

            <h4 className="font-serif font-bold text-xl text-[#E8B86D] mb-4">
              Our vision is to see:
            </h4>

            {/* 6 Vision Pillars Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {visionPillars.map((pillar, idx) => {
                const IconComponent = pillar.icon;
                return (
                  <div
                    key={idx}
                    className="bg-white/10 hover:bg-white/15 backdrop-blur-sm p-5 rounded-2xl border border-white/15 transition-all"
                  >
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center mb-3 text-white">
                      <IconComponent className="w-5 h-5 text-[#E8B86D]" />
                    </div>
                    <h5 className="font-serif font-bold text-[16px] text-white mb-1.5">
                      {pillar.title}
                    </h5>
                    <p className="text-xs text-white/80 leading-relaxed">
                      {pillar.desc}
                    </p>
                  </div>
                );
              })}
            </div>

            <p className="text-white/85 text-[14px] sm:text-[15px] leading-[1.8] border-t border-white/20 pt-6">
              We seek to be a Church where faith becomes visible in service, renewal becomes a way of life, and the light of Christ shines through every congregation and every member. This reflects the Diocese's wider commitment to Gospel proclamation, spiritual formation, care for the marginalized, education, healing, peace, justice, compassion and creation care.
            </p>
          </div>

          <div className="absolute -right-10 -bottom-10 opacity-10 pointer-events-none">
            <GiChurch className="w-96 h-96 text-white" />
          </div>
        </div>

        {/* ── Episcopal Tradition & Former / Retired Bishops ── */}
        <div className="bg-white p-8 sm:p-10 rounded-[28px] border border-[#E8DDD7] shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#FAF6F2] text-[#7B1E3A] flex items-center justify-center">
              <GiChurch className="w-6 h-6" />
            </div>
            <div>
              <span className="text-[#D4A64A] font-sans font-bold text-xs uppercase tracking-[0.2em] block">
                EPISCOPAL TRADITION & HERITAGE
              </span>
              <h3 className="font-serif font-bold text-[#7B1E3A] text-2xl sm:text-3xl">
                Former & Retired Bishops
              </h3>
            </div>
          </div>

          <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans mb-6">
            The Diocese honors the episcopal legacy of the Bishops and missionary leaders who preceded us. Their pastoral oversight, theological wisdom, and missionary zeal guided the Church through transformative eras in the erstwhile North Kerala Diocese and the present CSI Diocese of Malabar.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-[#FAF7F2] p-6 rounded-2xl border border-[#E8DDD7] mb-6">
            <div>
              <h4 className="font-serif font-bold text-[#7B1E3A] text-lg mb-2">Pastoral Legacy & Foundation</h4>
              <p className="text-sm text-[#5F5A58] leading-relaxed">
                From the earliest Basel Mission pioneers to the former Bishops of North Kerala, their leadership established the vast network of parishes, educational institutions, hospitals, and social welfare ministries that continue to flourish today.
              </p>
            </div>
            <div>
              <h4 className="font-serif font-bold text-[#7B1E3A] text-lg mb-2">The Malabar Episcopate</h4>
              <p className="text-sm text-[#5F5A58] leading-relaxed">
                Following the bifurcation that created the CSI Diocese of Malabar, the episcopal seat continues to guide the spiritual and missionary journey of our 26+ dioceses and 4.5M+ members under the grace of God.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
            <p className="text-sm text-[#7B6B66] italic">
              Remembering with prayerful gratitude all former Bishops, retired clergy, and pioneer missionaries.
            </p>
            <button
              onClick={() => onSelectSection('former-bishops')}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-white border border-[#E8DDD7] hover:border-[#7B1E3A] text-[#7B1E3A] text-xs font-bold uppercase tracking-wider rounded-full transition-all duration-300 shadow-sm cursor-pointer"
            >
              <span>View Former Bishops Archives</span>
              <LuArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Ministry and Mission */}
        <div>
          <h3 className="font-serif font-bold text-[#333A46] text-xl sm:text-2xl mb-4">Ministry and Mission</h3>
          <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans mb-6">
            Since its formation, CSI has remained deeply committed to ministry that is both spiritual and social. Its ministries include:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-6">
            {[
              'Pastoral care and worship', 'Mission and evangelism', 'Christian education',
              'Youth ministry', 'Women’s fellowship', 'Ecological stewardship',
              'Dalit and Adivasi concerns', 'Social justice and community development',
              'Medical and healing ministries', 'Child care and support for vulnerable communities'
            ].map(min => (
              <div key={min} className="flex items-center gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#7B1E3A]"></span>
                <span className="text-[#5F5A58] font-sans">{min}</span>
              </div>
            ))}
          </div>
          <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans">
            The Church administers schools, colleges, hospitals, care homes, theological institutions, and social outreach programmes across South India, serving people regardless of religion, caste, or social background.
          </p>
        </div>

        {/* Ecumenical Witness & CSI Malabar */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <h3 className="font-serif font-bold text-[#333A46] text-xl sm:text-2xl mb-4">Ecumenical Witness</h3>
            <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans mb-6">
              CSI continues to play an active role in the worldwide ecumenical movement. The Church is part of the global fellowship of:
            </p>
            <ul className="space-y-3 text-[#5F5A58] text-[16px] sm:text-[17px] font-sans mb-6">
              {[
                'World Council of Churches (WCC)',
                'Anglican Communion',
                'Christian Conference of Asia (CCA)',
                'World Communion of Reformed Churches',
                'National Council of Churches in India'
              ].map(org => (
                <li key={org} className="flex items-start gap-3">
                  <LuGlobe className="w-5 h-5 text-[#D4A64A] shrink-0 mt-1" />
                  <span>{org}</span>
                </li>
              ))}
            </ul>
            <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans">
              Through these relationships, CSI continues to contribute to global conversations on unity, mission, justice, peace, and reconciliation.
            </p>
          </div>
          <div>
            <h3 className="font-serif font-bold text-[#333A46] text-xl sm:text-2xl mb-4">CSI and the Diocese of Malabar</h3>
            <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans mb-4">
              The CSI Diocese of Malabar proudly shares in this rich heritage of faith, mission, and unity.
            </p>
            <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans mb-4">
              Rooted in the witness of the Basel Mission and nurtured within the wider life of the Church of South India, the Diocese continues its ministry through worship, mission, education, healing, and compassionate service throughout northern Kerala and beyond.
            </p>
            <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85] font-normal font-sans">
              As part of CSI, we remain committed to proclaiming Christ’s love, strengthening the Church, and serving society with faithfulness and compassion.
            </p>
          </div>
        </div>

        <div className="bg-[#FAF7F2] border-t-4 border-[#D4A64A] p-8 sm:p-10 text-center rounded-2xl shadow-sm border-x border-b border-[#E8DDD7]">
          <GiChurch className="w-10 h-10 text-[#D4A64A] mx-auto mb-4 opacity-50" />
          <p className="font-serif italic text-xl sm:text-2xl text-[#7B1E3A] leading-relaxed max-w-2xl mx-auto">
            “The Church of South India affirms that the Church is the servant of God to carry on the mission rooted in Jesus Christ and based on the Scriptures.”
          </p>
        </div>

        {!isEmbedded && (
          <div className="text-center pt-8">
            <button 
              onClick={() => onSelectSection('about')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#7B1E3A] hover:bg-[#5E132A] text-white font-sans font-bold text-sm tracking-wider uppercase rounded-full transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-1 cursor-pointer"
            >
              Return to About Diocese
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
